import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {translateConnectionObjectToArray} from 'nutshell-core';

import type {CoreEntityType} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Modal} from 'shells/modal/modal';

import type {
    EditionStub as Edition,
    DripSequencePreviewStubFragment,
    DripSequencePreviewStubFragment_editions_edges_node as DripEdition,
    GetEditionsForPreviewList as GetEditionsForPreviewListQuery,
    GetEditionsForPreviewListVariables as GetEditionsForPreviewListQueryVariables,
    GetDripSequencesForPreviewList as GetDripSequencesForPreviewListQuery,
    GetDripSequencesForPreviewListVariables as GetDripSequencesForPreviewListQueryVariables,
} from 'nutshell-graphql-types';

import type {SelectionQuery} from '../bulk-email/bulk-email-modals/bulk-personal-email-modal';

import GET_DRIP_SEQUENCES_FOR_PREVIEW_LIST from './graphql/queries/get-drip-sequences-for-preview-list.graphql';
import GET_EDITIONS_FOR_PREVIEW_LIST from './graphql/queries/get-editions-for-preview-list.graphql';
import {ReadyForSendingPage} from './ready-for-sending-page';
import {SelectMarketingOutreachListPage} from './select-marketing-outreach-list-page';
import {getOutreachItemType} from './send-marketing-outreach-helpers';
import {SendingConfirmationPage} from './sending-confirmation-page';

// Type for drip sequence + editions
export type DripSequence = DripSequencePreviewStubFragment & {
    editions: DripEdition[];
};

// Mixed type of Edition + DripSequence
export type OutreachItem = DripSequence | Edition;

// Type containing minimal data needed from sent outreach item
export type SentOutreachItem = {
    outreachType: 'Broadcast' | 'Newsletter' | 'DripSequence';
    name: string;
    htmlUrlPath: string;
    sentFromDraft?: boolean;
};

export type Props = {
    onClose: () => void;
    numRecipients: number;
    entityType: CoreEntityType;
    query: SelectionQuery;
    massSelect: boolean;
    selectedItems: string[];
    omittedItems: string[];
    canAccessFullCampaigns: boolean;
};

export function SendMarketingOutreachModal(props: Props) {
    const [selectedOutreachItem, setSelectedOutreachItem] = React.useState<
        OutreachItem | null | undefined
    >();
    const [sentOutreachItem, setSentOutreachItem] = React.useState<
        SentOutreachItem | null | undefined
    >();

    // Most recent broadcast and newsletter draft editions
    const {
        data: draftEditionsData,
        loading: isLoadingDraftEditions,
        error: errorLoadingDraftEditions,
    } = useQuery<GetEditionsForPreviewListQuery, GetEditionsForPreviewListQueryVariables>(
        GET_EDITIONS_FOR_PREVIEW_LIST,
        {
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-and-network',
            variables: {
                first: 100,
                statuses: ['NEW', 'SCHEDULED'],
                campaignTypes: ['BROADCAST', 'NEWSLETTER'],
            },
        }
    );

    // Draft editions
    const draftEditions = draftEditionsData
        ? translateConnectionObjectToArray(draftEditionsData.editions)
        : undefined;

    // Most recently sent broadcast and newsletter editions
    const {
        data: sentEditionsData,
        loading: isLoadingSentEditions,
        error: errorLoadingSentEditions,
    } = useQuery<GetEditionsForPreviewListQuery, GetEditionsForPreviewListQueryVariables>(
        GET_EDITIONS_FOR_PREVIEW_LIST,
        {
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-and-network',
            variables: {
                first: 100,
                statuses: ['SENT'],
                campaignTypes: ['BROADCAST', 'NEWSLETTER'],
                sort: 'SCHEDULED_TIME_DESC',
            },
        }
    );

    // Sent editions list
    const sentEditions = sentEditionsData
        ? translateConnectionObjectToArray(sentEditionsData.editions)
        : undefined;

    // Most recently activated drip sequences
    const {
        data: activeDripsData,
        loading: isLoadingActiveDrips,
        error: errorLoadingActiveDrips,
    } = useQuery<GetDripSequencesForPreviewListQuery, GetDripSequencesForPreviewListQueryVariables>(
        GET_DRIP_SEQUENCES_FOR_PREVIEW_LIST,
        {
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-and-network',
            variables: {
                limit: 100, // Arbitrary limit
                sort: 'MODIFIED_TIME_DESC',
                statuses: ['ACTIVE'],
                archived: false,
            },
        }
    );

    // Active drips list
    const activeDrips = activeDripsData
        ? translateConnectionObjectToArray(activeDripsData.dripSequences).map((dripSequence) => ({
              ...dripSequence,
              editions: translateConnectionObjectToArray(dripSequence.editions),
          }))
        : undefined;

    let modalContent;
    if (!selectedOutreachItem && !sentOutreachItem) {
        modalContent = (
            <SelectMarketingOutreachListPage
                onClickCancel={props.onClose}
                onSelectOutreachItem={(selectedItem: OutreachItem) => {
                    setSelectedOutreachItem(selectedItem);
                }}
                // Drafts that are ready to be sent
                draftEditions={draftEditions}
                isLoadingDraftEditions={isLoadingDraftEditions}
                // @ts-expect-error
                errorLoadingDraftEditions={safelyGetGraphQLErrorMessage(errorLoadingDraftEditions)}
                // Editions that have been sent
                sentEditions={sentEditions}
                isLoadingSentEditions={isLoadingSentEditions}
                // @ts-expect-error
                errorLoadingSentEditions={safelyGetGraphQLErrorMessage(errorLoadingSentEditions)}
                // Active drip sequences
                activeDripSequences={activeDrips}
                isLoadingActiveDripSequences={isLoadingActiveDrips}
                errorLoadingActiveDripSequences={safelyGetGraphQLErrorMessage(
                    // @ts-expect-error
                    errorLoadingActiveDrips
                )}
                canAccessFullCampaigns={props.canAccessFullCampaigns}
            />
        );
    } else if (selectedOutreachItem && !sentOutreachItem) {
        modalContent = (
            <ReadyForSendingPage
                outreachItem={selectedOutreachItem}
                onClickBack={() => {
                    setSelectedOutreachItem(undefined);
                }}
                onOutreachSendSuccess={(sentOutreach: SentOutreachItem) => {
                    setSentOutreachItem(sentOutreach);
                }}
                numRecipients={props.numRecipients}
                entityType={props.entityType}
                query={props.query}
                isMassSelect={props.massSelect}
                omittedItems={props.omittedItems}
                selectedItems={props.selectedItems}
            />
        );
    } else if (sentOutreachItem) {
        modalContent = (
            <SendingConfirmationPage
                onClose={props.onClose}
                sentOutreachItem={sentOutreachItem}
                numRecipients={props.numRecipients}
            />
        );
    }

    return (
        <Modal
            isOpen={true}
            onClose={props.onClose}
            // @ts-expect-error
            headerText={getModalHeaderText(selectedOutreachItem, sentOutreachItem)}
            // Width and height of this modal depends which page we are on
            isWide={!selectedOutreachItem}
            maxHeight={!selectedOutreachItem ? 700 : 600}
        >
            {modalContent}
        </Modal>
    );
}

function getModalHeaderText(
    selectedItem?: OutreachItem | null,
    sentOutreachItem?: SentOutreachItem | null
): string | null | undefined {
    if (sentOutreachItem) {
        return;
    }
    if (!selectedItem) {
        return 'Select a message';
    }

    if (getOutreachItemType(selectedItem) === 'DRIP_SEQUENCE') {
        return 'Ready to add to drip sequence';
    } else {
        return selectedItem.status === 'NEW' ? 'Ready for sending' : 'Ready to duplicate and send';
    }
}
